import { useSelector } from "react-redux";
import {
  DASHBOARD,
  LOGIN,
  VENDOR_DASHBOARD,
  PROFILE_UPDATE,
} from "./component/common/Routes/AppRoutes";
import {
  CUSTOMER_DASHBOARD,
  CUSTOMER_PROFILE_UPDATE,
} from "./component/common/Routes/CustomerRoutes";
import { useCallback, useEffect, useState } from "react";
import Logo from "./assets/AtithisevaFavIcon.jpg";
import { BrowserRouter } from "react-router-dom";
import { AppContent } from "./AppContent";
import { NEW_BOOKING_PAGES } from "./component/common/Routes/NewBookingPages";
import { onMessageListener, requestForToken } from "./firebase";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch } from "react-redux";
import { updateUserRequest } from "./redux/ProfileUpdate/profileUpdateAction";
import { updateCustomerRequest } from "./redux/Customers/actions/customerActions";
import { updateVendorRequest } from "./redux/Vendors/actions/vendorActions";
import { staffUpdateRequest } from "./redux/Staff/StaffAction";

const NotificationContent = ({ title, body, image }) => (
  <div className="notification-content">
    {image && (
      <img src={image} alt="Notification" className="notification-image" />
    )}
    <div className="notification-text">
      <div className="notification-title">{title}</div>
      <div className="notification-body">{body}</div>
    </div>
  </div>
);

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(Logo);
  const dispatch = useDispatch();

  const reduxIsAuthenticated = useSelector(
    (state) => state.auth.isAuthenticated,
  );
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    const link =
      document.querySelector("link[rel*='icon']") ||
      document.createElement("link");
    link.rel = "icon";
    link.href = faviconUrl;
    document.getElementsByTagName("head")[0].appendChild(link);
  }, [faviconUrl]);

  // Function to check if user has valid tokens
  const hasValidTokens = useCallback(() => {
    // Check sessionStorage first
    const accessToken = sessionStorage.getItem("accessToken");
    const refreshToken = sessionStorage.getItem("refreshToken");
    const instantToken = sessionStorage.getItem("instantToken");
    const newBookingToken = sessionStorage.getItem("newBookingAccessToken");

    const isOnNewBookingPage = NEW_BOOKING_PAGES.includes(
      window.location.pathname,
    );

    // Handle new booking flow
    if (newBookingToken && isOnNewBookingPage) {
      return false; // Not authenticated with regular tokens
    }

    return !!(accessToken || refreshToken || instantToken);
  }, []);

  // Initialize authentication state
  useEffect(() => {
    const checkAuthState = () => {
      // Force a check and cleanup if needed
      const newBookingToken = localStorage.getItem("newBookingAccessToken");
      if (newBookingToken) {
        // If new booking token exists, ensure other tokens are cleared
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("instantToken");
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      const isAuth = hasValidTokens();
      setIsAuthenticated(isAuth);
      setIsLoading(false);
    };

    checkAuthState();
  }, [hasValidTokens]);

  // Listen for auth state changes from other tabs
  useEffect(() => {
    const handleAuthStateChange = (event) => {
      const { type } = event.detail;

      if (type === "LOGOUT" || type === "TOKEN_REMOVED") {
        setIsAuthenticated(false);
        setIsDrawerOpen(false);
      }
    };

    window.addEventListener("authStateChange", handleAuthStateChange);

    return () => {
      window.removeEventListener("authStateChange", handleAuthStateChange);
    };
  }, []);

  // Update authentication state when user or Redux auth state changes
  useEffect(() => {
    const isAuth = hasValidTokens() || reduxIsAuthenticated;

    if (!isAuth) {
      setIsDrawerOpen(false);
      setIsAuthenticated(false);
    } else {
      setIsAuthenticated(true);
    }
  }, [user, reduxIsAuthenticated, hasValidTokens]);

  // Listen for storage changes (for cross-tab synchronization)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "newBookingAccessToken") {
        if (event.newValue) {
          // New booking token added, clear auth state and other tokens
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("instantToken");
          setIsAuthenticated(false);
          setIsDrawerOpen(false);
        } else {
          // New booking token removed, check if we should restore auth
          const isAuth = hasValidTokens();
          setIsAuthenticated(isAuth);
        }
      } else if (
        (event.key === "accessToken" ||
          event.key === "refreshToken" ||
          event.key === "instantToken") &&
        !event.newValue
      ) {
        // Auth tokens removed, update state only if no new booking token
        const newBookingToken = localStorage.getItem("newBookingAccessToken");
        if (!newBookingToken) {
          setIsAuthenticated(false);
          setIsDrawerOpen(false);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [hasValidTokens]);

  const handleDrawerToggle = () => setIsDrawerOpen(!isDrawerOpen);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const getDashboardRoute = () => {
    // If we're still loading or no tokens exist, go to login
    if (!isAuthenticated) {
      return LOGIN;
    }

    // If tokens exist but user data isn't loaded yet, wait
    if (!user && isAuthenticated) {
      return LOGIN;
    }

    // Handle initial redirect based on login status (for social login)
    if (user?.login_status === 1) {
      return CUSTOMER_PROFILE_UPDATE;
    }

    // Route based on user role (support both role and user_type_role_name)
    const activeRole = (
      user?.role ||
      user?.user_type_role_name ||
      ""
    ).toLowerCase();
    switch (activeRole) {
      case "admin":
      case "staff":
        return DASHBOARD;
      case "customer":
        return CUSTOMER_DASHBOARD;
      case "vendor":
        return VENDOR_DASHBOARD;
      default:
        return LOGIN;
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      requestForToken().then((token) => {
        if (token) {
          // Sync token based on user role
          const updateData = { deviceToken: token };

          const normalizedRole = (
            user?.role ||
            user?.user_type_role_name ||
            ""
          ).toLowerCase();
          if (normalizedRole === "customer" && user?.customer_id) {
            dispatch(
              updateCustomerRequest({
                ...updateData,
                ...user,
                status: 1,
                phone: user.contact,
                display_name: user.first_name + " " + user.last_name,
                id: user.customer_id,
              }),
            );
          } else if (normalizedRole === "vendor" && user?.vendor_id) {
            dispatch(
              updateVendorRequest({
                ...updateData,
                ...user,
                phone: user.contact,
                status: 1,
                display_name: user.first_name + " " + user.last_name,
                id: user.vendor_id,
              }),
            );
          } else if (
            normalizedRole === "admin" &&
            (user?.id || user?.user_id)
          ) {
            dispatch(
              updateUserRequest({
                ...updateData,
                ...user,
                status: 1,
                phone: user.contact,
                display_name: user.first_name + " " + user.last_name,
                id: user.id || user.user_id,
              }),
            );
          } else if (normalizedRole === "staff" && user?.staff_id) {
            dispatch(
              staffUpdateRequest(
                {
                  ...updateData,
                  ...user,
                  phone: user.contact,
                  status: 1,
                  display_name: user.first_name + " " + user.last_name,
                },
                user.staff_id,
              ),
            );
          }
        }
      });
    }

    const unsubscribe = onMessageListener((payload) => {
      console.log("Foreground notification payload:", payload);

      const title =
        payload.notification?.title || payload.data?.title || "New Message";
      const body = payload.notification?.body || payload.data?.body || "";
      const image =
        payload.notification?.image ||
        payload.data?.image ||
        payload.notification?.icon ||
        payload.data?.icon;

      // // 1. Show in-app toast FIRST (most reliable)
      // try {
      //   toast.info(
      //     <NotificationContent title={title} body={body} image={image} />,
      //     {
      //       position: "bottom-right",
      //       autoClose: 8000,
      //       className: "custom-toast",
      //     },
      //   );
      // } catch (toastError) {
      //   console.error("Error showing toast:", toastError);
      // }

      // 2. Show system notification
      if (Notification.permission === "granted") {
        try {
          navigator.serviceWorker.ready
            .then((registration) => {
              registration.showNotification(title, {
                body: body,
                icon: image, // Use image as icon for system notification if available
                image: image,
                data: payload.data,
              });
            })
            .catch((swError) => {
              console.error("Service Worker registration error:", swError);
            });
        } catch (sysError) {
          console.error("Error initiating system notification:", sysError);
        }
      } else {
        console.log(
          "Notification permission not granted:",
          Notification.permission,
        );
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isAuthenticated, user]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <ToastContainer />
      <BrowserRouter>
        <AppContent
          isAuthenticated={isAuthenticated}
          user={user}
          isDrawerOpen={isDrawerOpen}
          handleDrawerToggle={handleDrawerToggle}
          anchorElUser={anchorElUser}
          handleOpenUserMenu={handleOpenUserMenu}
          handleCloseUserMenu={handleCloseUserMenu}
          getDashboardRoute={getDashboardRoute}
        />
      </BrowserRouter>
    </>
  );
};
export default App;
